<template>
    <legal-page title="Terms of Use">
        <template #default="{legal}">
            <p>
                These Terms of Use ("Terms") govern your use of {{ legal.brand }}, including our website, student application, courses,
                conferences, and related services (the "Services"), operated by {{ legal.company }}, doing business as {{ legal.tradeName }} ("we," "us," or "our").
                <strong>By creating an account, logging in, or otherwise using the Services, you agree to these Terms and to our
                <router-link to="/privacy">Privacy Policy</router-link>.</strong> If you do not agree, do not use the Services.
            </p>

            <h3>1. Who May Use the Services</h3>
            <p>
                The Services are intended for educators, coaches, and other adults seeking professional development. You must be at
                least 18 years old to create an account. You agree to provide accurate information, including your name as it should
                appear on certificates and records, and to keep it current.
            </p>

            <h3>2. Your Account</h3>
            <p>
                You may log in with a password or with a one-time access code sent to your e-mail address or phone number. You are
                responsible for keeping your password, e-mail account, and phone secure, and for activity that occurs under your
                account. Each account is for one person; do not share it or complete coursework for someone else. Tell us promptly at
                <a :href="`mailto:${legal.supportEmail}`">{{ legal.supportEmail }}</a> if you believe your account has been accessed
                without your permission. We may merge duplicate accounts that belong to the same person.
            </p>

            <h3>3. Courses, Hours, and Credit</h3>
            <p>
                Courses, clinics, and conferences are subject to the requirements described for each offering, including access
                periods, required time, lessons, study plans, reflections, and applications. Access to a course may end on its
                expiration date or after completion. Some courses limit how often they can be repeated.
            </p>
            <p>
                We award clock hours, certificates, and other completion records only when the applicable requirements are met. Time
                spent in a course may be recorded automatically while a course is open. <strong>Whether a school district, state
                agency, athletic association, or other organization accepts our hours or certificates (for salary advancement,
                certification renewal, coaching eligibility, or any other purpose) is determined by that organization.</strong> You
                are responsible for confirming that an offering meets your requirements before you purchase it.
            </p>

            <h3>4. Your Work and Academic Integrity</h3>
            <p>
                Study plans, reflections, applications, uploaded files, and other materials you submit must be your own work and
                accurately describe the time and activities you completed. Submissions are reviewed by our staff, and automated tools,
                including artificial intelligence, may assist in that review. We may request changes to, decline, or revoke credit for
                submissions that do not meet requirements or that we reasonably believe are inaccurate or not your own.
            </p>
            <p>
                You keep ownership of what you submit. You grant us a non-exclusive license to store, review, and use your submissions
                as needed to provide the Services, issue records, and verify completion.
            </p>

            <h3>5. Purchases and Payment Plans</h3>
            <p>
                Prices, access terms, and any refund conditions are shown at checkout and in the purchase acknowledgment you sign.
                Card payments are processed by our payment processor; we do not store full card numbers.
            </p>
            <p>
                Payment plans and other financing are provided or serviced by third-party financing partners under a separate
                agreement between you and that partner. That agreement, not these Terms, governs your payment obligations, payment
                schedule, fees, interest, and default. You remain responsible for paying for the Services you purchase, whether you
                pay us directly or through a financing partner.
            </p>
            <p>
                <strong>Non-payment.</strong> If a payment is declined, reversed, or not made when due, whether it is owed to us or
                to a financing partner, or if a payment plan is not in good standing, we may suspend or restrict access to the
                courses, conferences, and other Services connected to that purchase, or to your account, and may withhold clock
                hours, certificates, and other completion records until the balance is resolved.
            </p>

            <h3>6. E-mail and Text Messages</h3>
            <p>
                We send e-mail about your account and purchases, such as access codes, password resets, and enrollment and registration confirmations. These
                account messages are required to use the Services. We also send optional messages, such as course progress
                notifications and monthly check-ins, which you can turn off at any time in your account settings or with the
                unsubscribe link in the message.
            </p>
            <h4>Text Messages</h4>
            <p>
                {{ legal.brand }} sends text messages to the mobile number you provide, from {{ legal.smsNumber }}:
            </p>
            <ul>
                <li><strong>Texts you request</strong>, such as a login access code. These are sent only when you ask for one.</li>
                <li><strong>Account and purchase texts</strong> about a purchase you started with us, such as a link to complete
                    your enrollment. These relate to transactions you started and are not marketing.</li>
                <li><strong>Notification texts</strong>, such as when a study plan, reflection, or application is reviewed. These are
                    sent only if you opt in, during sign-up or in your account settings.</li>
            </ul>
            <p>
                Message frequency varies. <strong>Message and data rates may apply.</strong> Reply <strong>STOP</strong> to stop
                text messages and <strong>HELP</strong> for help, or contact
                <a :href="`mailto:${legal.supportEmail}`">{{ legal.supportEmail }}</a>. After you reply STOP, texts you request,
                such as login codes, may not be delivered; you can still log in by e-mail or password. Consent to receive text
                messages is not a condition of any purchase. Carriers are not liable for delayed or undelivered messages. Texts from a
                financing partner about a payment plan are governed by your agreement with that partner.
            </p>

            <h3>7. Acceptable Use</h3>
            <p>You agree not to:</p>
            <ul>
                <li>copy, redistribute, sell, or publicly share course content except as the course allows;</li>
                <li>use automated means to access the Services, inflate recorded time, or bypass course requirements;</li>
                <li>interfere with the security or operation of the Services, or access another person's account;</li>
                <li>submit content that is unlawful, infringing, or that you do not have the right to share.</li>
            </ul>

            <h3>8. Our Content</h3>
            <p>
                Courses, materials, software, and branding in the Services are owned by us or our licensors. We grant you a limited,
                personal, non-transferable license to use them for your own professional development during your access period.
            </p>

            <h3>9. Suspension, Restriction, and Termination</h3>
            <p>We may suspend, restrict, or disable your account, or access to any part of the Services, at our discretion, including when:</p>
            <ul>
                <li>a payment owed to us or to a financing partner is past due, declined, or reversed (see section 5);</li>
                <li>a financing partner, school district, or other partner organization requires it, or its requirements for your enrollment are not met;</li>
                <li>you do not meet the requirements of a course, program, or certification;</li>
                <li>you violate these Terms, or we reasonably suspect fraud, misuse, or a risk to other users or the Services; or</li>
                <li>we are required to by law.</li>
            </ul>
            <p>
                Where practical, we will tell you why and what is needed to restore access. Suspending or restricting your account
                does not cancel any amount you owe. You may stop using the Services at any time. Completion records already issued
                remain part of our records as described in the Privacy Policy.
            </p>

            <h3>10. Disclaimers</h3>
            <p>
                The Services are provided "as is" and "as available." To the fullest extent permitted by law, we disclaim all
                warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and
                non-infringement. We do not guarantee that the Services will be uninterrupted or error-free, or that any third party
                will accept our hours or certificates.
            </p>

            <h3>11. Limitation of Liability</h3>
            <p>
                To the fullest extent permitted by law, we are not liable for indirect, incidental, special, consequential, or punitive
                damages, or for lost profits or data. Our total liability for any claim relating to the Services is limited to the
                amount you paid us for the specific offering giving rise to the claim during the twelve months before the claim.
            </p>

            <h3>12. Governing Law</h3>
            <p>
                These Terms are governed by the laws of the State of {{ legal.governingState }}, without regard to its conflict-of-law
                rules. Any dispute will be resolved in the state or federal courts located in Spokane County, {{ legal.governingState }}, and you
                consent to their jurisdiction.
            </p>

            <h3>13. Changes to These Terms</h3>
            <p>
                We may update these Terms from time to time. The effective date above shows when they last changed. Continuing to use
                the Services after a change takes effect means you accept the updated Terms.
            </p>

            <h3>14. Contact Us</h3>
            <p>
                {{ legal.company }}<br/>
                {{ legal.mailingAddress }}<br/>
                {{ legal.phone }}<br/>
                <a :href="`mailto:${legal.supportEmail}`">{{ legal.supportEmail }}</a>
            </p>
        </template>
    </legal-page>
</template>

<script>
    import LegalPage from '@/components/legal/LegalPage';

    export default {
        name: 'Terms',
        components: {LegalPage},
    };
</script>
